import {
  readRootFormOnLoadScript,
  shouldRunRootFormOnLoadOnClientBeforeBuild,
} from '@/common/utils/formRootOnLoad'
import { mergeExchangeStatesByKey } from '@/common/utils/normalizeExchangeStates'
import { mergeFieldMaps } from '@/common/utils/jsonUniData'
import {
  mergeOpenFormFieldMapsForOnLoad,
  syncHiddenTextToVisibleHtmlFields,
} from '@/common/utils/syncHiddenTextToVisibleHtmlFields'
import type { AppStore } from '@/stores/app'
import actionScript from '@/services/action/actionScript'
import { normalizeDispatchedVisibilities } from '@/services/action/applyDispatchedVisibilities'
import type { ActionContext, ActionDependencies, ExchangeData } from '@/services/action/types'
import { createEmptyExchangeData } from '@/services/action/types'
import {
  dropStaleEditableAfterVisibilityShow,
  editablePatchesFromDispatched,
  recordSetEditableByLevelPatch,
  recordSetEditableNamePatch,
  recordSetVisibleByLevelPatch,
  recordSetVisibleNamePatch,
  type VisibilityStatePatch,
} from '@/common/utils/visibilityStatePatches'
import { formHasRenderableLayout, parseService } from '@/services/parse/parse.service'
import { resolveFormSchemaIdFromPageEntry } from '@/common/utils/resolvePageIdentity'

function resolveResponseFormRoot(response: Record<string, unknown>): Record<string, unknown> | null {
  let form = response.form
  if (typeof form === 'string') {
    const formId = resolveFormSchemaIdFromPageEntry(response, response.status)
    const parsed = parseService.getForm(form, Number.isFinite(formId) ? formId : 0)
    if (formHasRenderableLayout(parsed)) {
      form = parsed
    } else {
      return null
    }
  }
  if (!form || typeof form !== 'object' || Array.isArray(form)) {
    return null
  }
  const root = form as Record<string, unknown>
  if (shouldRunRootFormOnLoadOnClientBeforeBuild(root)) {
    return root
  }
  const nested = root.form
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    if (shouldRunRootFormOnLoadOnClientBeforeBuild(nested)) {
      return nested as Record<string, unknown>
    }
  }
  return root
}

function resolveOnLoadCurrentData(response: Record<string, unknown>): ExchangeData {
  const changed = (response.jsResult as { changed_data?: ExchangeData } | undefined)?.changed_data
  const mergedFields = mergeOpenFormFieldMapsForOnLoad(response)
  const base = changed && typeof changed === 'object' ? changed : createEmptyExchangeData()
  const fieldsRoot = (base.fields ?? {}) as Record<string, unknown>
  const existingFields = (fieldsRoot.fields ?? {}) as Record<string, unknown>

  return {
    ...base,
    fields: {
      ...fieldsRoot,
      fields: mergeFieldMaps(existingFields, mergedFields),
    },
  } as ExchangeData
}

function mergeScriptResultIntoActionResponse(
  response: Record<string, unknown>,
  updatedChangedData: ExchangeData,
  visibilityStates: VisibilityStatePatch[],
  formRoot: Record<string, unknown> | null,
): Record<string, unknown> {
  const scriptFields = updatedChangedData?.fields?.fields ?? {}
  const jsResult = (response.jsResult ?? {}) as Record<string, unknown>
  const changedData = (jsResult.changed_data ?? {}) as Record<string, unknown>
  const changedFieldsRoot = (changedData.fields ?? {}) as Record<string, unknown>
  const existingChangedFields = (changedFieldsRoot.fields ?? {}) as Record<string, unknown>

  const uniData = (response.uniData ?? {}) as Record<string, unknown>
  const uniFieldsRoot = (uniData.fields ?? {}) as Record<string, unknown>
  const existingUniFields = (uniFieldsRoot.fields ?? {}) as Record<string, unknown>

  const openFormFields = mergeOpenFormFieldMapsForOnLoad(response)
  let mergedChangedFields = mergeFieldMaps(existingChangedFields, openFormFields, scriptFields)
  let mergedUniFields = mergeFieldMaps(existingUniFields, openFormFields, scriptFields)

  const syncedChanged = syncHiddenTextToVisibleHtmlFields(
    mergedChangedFields,
    visibilityStates,
    formRoot,
  )
  const syncedUni = syncHiddenTextToVisibleHtmlFields(mergedUniFields, visibilityStates, formRoot)
  mergedChangedFields = syncedChanged
  mergedUniFields = syncedUni

  const existingStates = Array.isArray(jsResult.states) ? jsResult.states : []
  const mergedStates =
    visibilityStates.length > 0
      ? dropStaleEditableAfterVisibilityShow(
          mergeExchangeStatesByKey(existingStates, visibilityStates),
          visibilityStates,
        )
      : existingStates

  const hasOnLoadSideEffects =
    visibilityStates.length > 0 || Object.keys(scriptFields).length > 0

  return {
    ...response,
    ...(hasOnLoadSideEffects ? { $preBuiltRootOnLoad: true } : {}),
    uniData: {
      ...uniData,
      fields: {
        ...uniFieldsRoot,
        fields: mergedUniFields,
      },
    },
    jsResult: {
      ...jsResult,
      states: mergedStates,
      changed_data: {
        ...changedData,
        fields: {
          ...changedFieldsRoot,
          fields: mergedChangedFields,
        },
      },
    },
  }
}

function wrapFormProviderForPreBuildVisibility(
  dependencies: ActionDependencies | undefined,
  visibilityStates: VisibilityStatePatch[],
  layoutForm?: unknown,
): ActionDependencies {
  const provider = dependencies?.formProvider ?? {}
  return {
    ...(dependencies ?? {}),
    formProvider: {
      ...provider,
      setVisible: (name: string, visible: boolean) => {
        recordSetVisibleNamePatch(visibilityStates, name, visible, layoutForm)
      },
      setVisibleByLevel: (level: string, id: string | number, visible: boolean) => {
        recordSetVisibleByLevelPatch(visibilityStates, level, id, visible, layoutForm)
      },
      setEditable: (name: string, editable: boolean) => {
        recordSetEditableNamePatch(visibilityStates, name, editable)
      },
      setEditableByLevel: (level: string, id: string | number, editable: boolean) => {
        recordSetEditableByLevelPatch(visibilityStates, level, id, editable)
      },
    },
  }
}

function appendDispatchedVisibilityPatches(
  patches: VisibilityStatePatch[],
  dispatched: unknown,
  layoutForm?: unknown,
): void {
  const normalized = normalizeDispatchedVisibilities(dispatched)
  for (const item of normalized) {
    if (item.id == null || item.id === '') {
      recordSetVisibleNamePatch(patches, item.level, item.visible, layoutForm)
      continue
    }
    recordSetVisibleByLevelPatch(patches, item.level, item.id, item.visible, layoutForm)
  }
}

/**
 * Выполняет корневой onLoad с web_client_exec="true" до setPageData:
 * currentData = jsResult.changed_data, результат мержится в response (поля + states).
 */
export async function applyWebClientFormOnLoadBeforeBuild(
  response: Record<string, unknown>,
  context: ActionContext,
  appStore: AppStore | undefined,
  dependencies: ActionDependencies | undefined,
  invokeNestedElementActionFromClientScript: (params: any) => Promise<ExchangeData | null>,
  openedFormId: string,
): Promise<Record<string, unknown>> {
  if (response.$preBuiltRootOnLoad === true) {
    return response
  }

  const formRoot = resolveResponseFormRoot(response)
  if (!formRoot || !shouldRunRootFormOnLoadOnClientBeforeBuild(formRoot)) {
    return response
  }

  const script = readRootFormOnLoadScript(formRoot)
  if (!script?.trim() || !appStore) {
    return response
  }

  const visibilityStates: VisibilityStatePatch[] = []
  const wrappedDependencies = wrapFormProviderForPreBuildVisibility(
    dependencies,
    visibilityStates,
    formRoot,
  )
  const onLoadContext: ActionContext = {
    ...context,
    formId: openedFormId,
    level: 'page',
  }

  const currentData = resolveOnLoadCurrentData(response)
  const updatedChangedData = await actionScript.executeClientCode(
    script,
    onLoadContext,
    appStore,
    currentData,
    invokeNestedElementActionFromClientScript,
    wrappedDependencies,
    {
      skipPageFieldSnapshot: true,
      pageDataStub: { form: formRoot },
    },
  )

  appendDispatchedVisibilityPatches(
    visibilityStates,
    updatedChangedData.jsResultProperties?.dispatched_visibilities,
    formRoot,
  )
  visibilityStates.push(
    ...editablePatchesFromDispatched(updatedChangedData.jsResultProperties?.dispatched_editables),
  )

  return mergeScriptResultIntoActionResponse(
    response,
    updatedChangedData,
    visibilityStates,
    formRoot,
  )
}

export type EnsureOpenFormPreBuildOptions = {
  appStore: AppStore
  openedFormId: string
  /** When true (default), parse form via parsePlatformActionForm before onLoad. */
  parseForm?: boolean
  parseActionId?: string | number
  dependencies?: ActionDependencies
  invokeNestedElementActionFromClientScript?: (params: any) => Promise<ExchangeData | null>
}

/**
 * Parse (optional) + pre-build web_client_exec root onLoad before setPageData on any open path.
 */
export async function ensureWebClientOnLoadBeforeOpenForm(
  pageData: Record<string, unknown>,
  options: EnsureOpenFormPreBuildOptions,
): Promise<Record<string, unknown>> {
  if (!pageData?.form || !options.appStore) {
    return pageData
  }
  if (pageData.$preBuiltRootOnLoad === true) {
    return pageData
  }

  if (options.parseForm !== false) {
    const { parsePlatformActionForm } = await import('@/services/core/core.service')
    await parsePlatformActionForm(
      pageData,
      options.parseActionId ?? options.openedFormId,
    )
  }

  return prepareOpenFormPageDataForStore(pageData, {
    appStore: options.appStore,
    openedFormId: options.openedFormId,
    dependencies: options.dependencies,
    invokeNestedElementActionFromClientScript: options.invokeNestedElementActionFromClientScript,
  })
}

/** Pre-build web_client_exec onLoad for openForm payloads (getWindowData / action open). */
export async function prepareOpenFormPageDataForStore(
  pageData: Record<string, unknown>,
  options: {
    appStore: AppStore
    openedFormId: string
    dependencies?: ActionDependencies
    invokeNestedElementActionFromClientScript?: (params: any) => Promise<ExchangeData | null>
  },
): Promise<Record<string, unknown>> {
  const context: ActionContext = {
    id: '0',
    name: 'openFormOnLoad',
    data: {},
    formId: options.openedFormId,
    level: 'page',
  }
  const invokeNested =
    options.invokeNestedElementActionFromClientScript ?? (async () => null)
  return applyWebClientFormOnLoadBeforeBuild(
    pageData,
    context,
    options.appStore,
    options.dependencies,
    invokeNested,
    options.openedFormId,
  )
}
